import { useEffect, useMemo, useState } from "react"
import { Modal, ModalBody, ModalHeader, ModalTitle } from "react-bootstrap"
import { Immagine } from "../types"

type ImagesResponse = {
  count?: number
  next?: string | null
  previous?: string | null
  results: Immagine[]
}

const PAGE_SIZE = 18

type ImagePreviewSelectFieldProps = {
  label: string
  value?: number | null
  initialImage?: Immagine | null
  required?: boolean
  placeholder?: string
  isValid?: boolean
  errorMessage?: string
  onChange: (value?: number) => void
  fetchImage: (id: number) => Promise<Immagine>
  fetchImages: (params: Record<string, string | number | Array<number>>) => Promise<ImagesResponse>
}

export function ImagePreviewSelectField({
  label,
  value,
  initialImage,
  required = false,
  placeholder = "Cerca immagine",
  isValid,
  errorMessage,
  onChange,
  fetchImage,
  fetchImages,
}: ImagePreviewSelectFieldProps) {
  const [showModal, setShowModal] = useState(false)
  const [search, setSearch] = useState("")
  const [options, setOptions] = useState<Immagine[]>([])
  const [page, setPage] = useState(1)
  const [count, setCount] = useState(0)
  const [selectedImage, setSelectedImage] = useState<Immagine | null>(initialImage || null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!showModal) {
      return
    }

    let active = true

    setLoading(true)
    const timeout = window.setTimeout(() => {
      fetchImages({ search, page, page_size: PAGE_SIZE })
        .then((data) => {
          if (active) {
            setOptions(data.results)
            setCount(data.count ?? data.results.length)
          }
        })
        .finally(() => {
          if (active) {
            setLoading(false)
          }
        })
    }, 250)

    return () => {
      active = false
      window.clearTimeout(timeout)
    }
  }, [fetchImages, page, search, showModal])

  useEffect(() => {
    if (!value) {
      setSelectedImage(null)
      return
    }

    if (selectedImage?.id === value) {
      return
    }

    if (initialImage?.id === value) {
      setSelectedImage(initialImage)
      return
    }

    fetchImage(value).then((image) => {
      setSelectedImage(image)
    })
  }, [fetchImage, initialImage, selectedImage?.id, value])

  const displayedOptions = useMemo(() => {
    if (!selectedImage || options.some((option) => option.id === selectedImage.id)) {
      return options
    }

    return [selectedImage, ...options]
  }, [options, selectedImage])

  const numPages = Math.max(1, Math.ceil(count / PAGE_SIZE))
  const firstVisible = count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const lastVisible = Math.min(page * PAGE_SIZE, count)

  return (
    <div className="row">
      <label className="col-4 col-form-label">
        {label}
        {required && <span className="text-danger ms-1">*</span>}
      </label>
      <div className="col-8">
        <div className="image-preview-select">
          <div className={`image-preview-select__field${isValid === false ? " is-invalid" : ""}`}>
            {selectedImage ? (
              <div className="image-preview-select__selected image-preview-select__selected--compact">
                <img
                  src={selectedImage.immagine_thumbnail || selectedImage.immagine_medium || selectedImage.immagine}
                  alt={selectedImage.titolo}
                />
                <div className="image-preview-select__selected-copy">
                  <div className="image-preview-select__selected-title">{selectedImage.titolo || "Senza titolo"}</div>
                  <div className="image-preview-select__meta">{selectedImage.titolo_completo_edizione}</div>
                </div>
              </div>
            ) : (
              <div className="image-preview-select__empty">Nessuna immagine selezionata</div>
            )}

            <div className="image-preview-select__actions">
              <button
                type="button"
                className="image-preview-select__button image-preview-select__button--primary"
                onClick={() => setShowModal(true)}
              >
                {selectedImage ? "Cambia" : "Seleziona"}
              </button>
              {value && (
                <button
                  type="button"
                  className="image-preview-select__button image-preview-select__button--remove"
                  onClick={() => {
                    setSearch("")
                    onChange(undefined)
                  }}
                >
                  Rimuovi
                </button>
              )}
            </div>
          </div>
          {errorMessage && isValid === false && <div className="invalid-feedback d-block">{errorMessage}</div>}
        </div>
      </div>

      <Modal centered show={showModal} onHide={() => setShowModal(false)} size="xl">
        <ModalHeader closeButton>
          <ModalTitle>{label}</ModalTitle>
        </ModalHeader>
        <ModalBody className="image-preview-select__modal">
          <input
            autoFocus
            className="form-control"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(1)
            }}
            placeholder={placeholder}
          />

          <div className="image-preview-select__pagination">
            <span>
              {count > 0
                ? `Immagini ${firstVisible}-${lastVisible} di ${count}`
                : loading
                  ? "Caricamento immagini..."
                  : "Nessuna immagine"}
            </span>
            <div className="image-preview-select__pagination-actions">
              <button
                type="button"
                className="image-preview-select__button image-preview-select__button--sm"
                disabled={page <= 1 || loading}
                onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
              >
                Precedente
              </button>
              <span>
                Pagina {page} di {numPages}
              </span>
              <button
                type="button"
                className="image-preview-select__button image-preview-select__button--sm"
                disabled={page >= numPages || loading}
                onClick={() => setPage((currentPage) => Math.min(numPages, currentPage + 1))}
              >
                Successiva
              </button>
            </div>
          </div>

          <div className="image-preview-select__grid" aria-busy={loading}>
            {displayedOptions.map((image) => {
              const selected = image.id === value
              return (
                <button
                  type="button"
                  key={image.id}
                  className={`image-preview-select__option${selected ? " image-preview-select__option--selected" : ""}`}
                  onClick={() => {
                    setSelectedImage(image)
                    onChange(image.id)
                    setShowModal(false)
                  }}
                >
                  <span className="image-preview-select__thumb">
                    <img src={image.immagine_thumbnail || image.immagine_medium || image.immagine} alt={image.titolo} />
                  </span>
                  <span className="image-preview-select__title">{image.titolo || "Senza titolo"}</span>
                  <span className="image-preview-select__meta">{image.titolo_completo_edizione}</span>
                </button>
              )
            })}
          </div>

          {loading && <div className="image-preview-select__status">Caricamento immagini...</div>}
          {!loading && displayedOptions.length === 0 && (
            <div className="image-preview-select__status">Nessuna immagine trovata</div>
          )}
        </ModalBody>
      </Modal>
    </div>
  )
}
